import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { useState, useEffect, useId } from "react";
import type { Company, CreateCompanyRequest } from "@/services/api/companies";

interface CompanyEditDialogProps {
  open: boolean;
  company: Company | null;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (id: string | number, data: CreateCompanyRequest) => void;
}

export function CompanyEditDialog({
  open,
  company,
  isLoading,
  onOpenChange,
  onSubmit,
}: CompanyEditDialogProps) {
  const [name, setName] = useState("");
  const [linkedInUrl, setLinkedInUrl] = useState("");

  const descriptionId = useId();

  useEffect(() => {
    if (company) {
      setName(company.name);
      setLinkedInUrl(company.linkedInUrl);
    }
  }, [company]);

  if (!company) return null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !linkedInUrl.trim()) return;

    onSubmit(company.id, {
      name: name.trim(),
      linkedInUrl: linkedInUrl.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={descriptionId}>
        <DialogHeader>
          <DialogTitle>Edit company</DialogTitle>
          <DialogDescription>
            Update the company information below.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-ink-800">
              Company name
            </label>
            <Input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Enter company name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-ink-800">
              LinkedIn URL
            </label>
            <Input
              value={linkedInUrl}
              onChange={(event) => setLinkedInUrl(event.target.value)}
              placeholder="https://www.linkedin.com/company/..."
            />
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !name.trim() || !linkedInUrl.trim()}
            >
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
